/* eslint-disable import/no-unused-modules */
import { useRouter } from 'next/router';
import GamePage from '../index';
import { GetStaticPropsContext } from 'next';
import { Game } from 'functions/src/types/firestore/Game';
import { Announcement } from 'functions/src/types/firestore/Game/Announcement';
import { GameMedia } from 'functions/src/types/firestore/Game/GameMedia';
import { db } from 'src/config/firebaseAdmin';
import { getFirestoreData } from 'src/util/getFirestoreData';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';
import { MediaProvider } from 'src/contexts/MediaContext';

type NestedGamePageProps = {
  game: Game<Date>;
  announcements: Announcement<Date>[];
  gameMedia: GameMedia[];
};

function NestedGamePage({
  game,
  announcements,
  gameMedia,
}: NestedGamePageProps) {
  const router = useRouter();
  const { tab } = router.query;
  return tab ? (
    <MediaProvider media={gameMedia}>
      <GamePage
        openTabName={tab as string}
        game={game}
        announcements={announcements}
      />
    </MediaProvider>
  ) : (
    ''
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const pid = context?.params?.pid;
  const baseRef = `Game/${pid}`;
  const gameDoc = await db
    .doc(baseRef)
    .withConverter(ConverterFactory.buildDateConverter<Game<Date>>())
    .get();
  const game = gameDoc.data();
  const announcements = await getFirestoreData(
    db
      .collection(`${baseRef}/Announcement`)
      .withConverter(ConverterFactory.buildDateConverter<Announcement<Date>>()),
  );
  const gameMedia = await getFirestoreData(
    db.collection(`${baseRef}/GameMedia`).orderBy('priority', 'desc'),
  );
  const clientId = process.env.NEXT_PUBLIC_TWITCH_CLIENT_ID;
  const clientSecret = process.env.TWITCH_CLIENT_SECRET;
  const authTokenRes = await fetch(
    `https://id.twitch.tv/oauth2/token?client_id=${clientId}&client_secret=${clientSecret}&grant_type=client_credentials`,
    {
      method: 'POST',
    },
  );
  const authTokenJson = await authTokenRes.json();
  const authToken = authTokenJson.access_token;
  const hideDeadStreams = async (media: GameMedia[]) => {
    if (!authToken || !clientId) {
      return media;
    }
    //first collect the user ids
    const twitchUserIds = media
      .filter((medium) => {
        return medium.type === 'twitch' && !medium.hidden;
      })
      .map((medium) => {
        return (medium as GameMedia & { type: 'twitch' }).channelId;
      });
    //check if streams are active
    const queryParams = twitchUserIds
      .map((id) => {
        return `user_id=${encodeURIComponent(id)}`;
      })
      .join('&');
    const { data } = await fetch(
      `https://api.twitch.tv/helix/streams?${queryParams}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Client-Id': clientId,
        },
      },
    ).then((response) => {
      return response.json();
    });
    return media
      .map((medium) => {
        if (medium.type !== 'twitch') {
          return medium;
        }
        const datum = data.find((stream) => {
          return stream.user_id === medium.channelId;
        });
        if (!!datum) {
          const username = (datum['user_name'] as string).toLowerCase();
          const thumbnailUrl = (datum['thumbnail_url'] as string)
            .replace('{width}', '220')
            .replace('{height}', '121');
          const viewerCount = datum['viewer_count'] as string;
          return {
            src: `https://www.twitch.tv/${username}`,
            type: 'twitch',
            thumbnailUrl,
            viewerCount,
          };
        }
        return undefined;
      })
      .filter((medium) => {
        return !!medium;
      });
  };
  const correctedGameMedia = await hideDeadStreams(gameMedia as GameMedia[]);

  return {
    props: {
      game,
      announcements,
      gameMedia: correctedGameMedia,
    },
    revalidate: 30,
  };
}

// TODO: uncomment when Mobile Legends is integrated
// TODO: add MetaDerby when integrated
export async function getStaticPaths() {
  const TABS_MAP = {
    'fantom-survivor': [
      'survivors',
      'mint-guide',
      'reviews',
      'transaction-activity',
      'system',
    ],
    'undead-blocks': [
      'weapons',
      'leaderboards',
      'staking',
      'reviews',
      'roadmap',
      'governance',
      'transaction-activity',
      'system',
    ],
    metaops: ['tournaments', 'reviews', 'system'],
    // 'mobile-legends': ['tournaments', 'reviews', 'system'],
    'big-time': ['tournaments', 'reviews', 'system'],
    'ev.io': ['tournaments', 'items', 'reviews', 'system'],
    'mini-royale-nations': [
      'tournaments',
      'reviews',
      'system',
      'transaction-activity',
      'leaderboards',
      'roadmap',
    ],
    'earth-from-another-sun': [
      'tournaments',
      'reviews',
      'system',
      'roadmap',
      'items',
    ],
    'planet-ix': [
      'tournaments',
      'reviews',
      'system',
      'roadmap',
      'transaction activity',
      'items',
    ],
    chronos: ['tournaments', 'reviews', 'system', 'roadmap'],
    'br1-infinite': [
      'tournaments',
      'reviews',
      'system',
      'transaction-activity',
      'leaderboards',
      'roadmap',
    ],
    'raini-the-lords-of-light': [
      'tournaments',
      'reviews',
      'roadmap',
      'transaction Activity',
      'system',
    ],
    'cards-of-ethernity': [
      'tournaments',
      'reviews',
      'system',
      'roadmap',
      'items',
    ],
    'the-bornless': ['tournaments', 'reviews', 'system', 'roadmap'],
    'mokens-league': ['tournaments', 'reviews', 'system', 'roadmap'],
    splinterlands: ['tournaments', 'reviews', 'system', 'roadmap'],
    'champions-ascension': ['tournaments', 'reviews', 'system', 'roadmap'],
    'the-forge-arena': ['tournaments', 'reviews', 'system', 'roadmap'],
    'planet-mojo': ['tournaments', 'reviews', 'system', 'roadmap'],
    'reign-of-terror': ['tournaments', 'reviews', 'system', 'roadmap'],
    shrapnel: ['tournaments', 'reviews', 'system', 'roadmap'],
    'wagmi-defense': ['tournaments', 'reviews', 'system', 'roadmap'],
    'world-war-0x': ['tournaments', 'reviews', 'system', 'roadmap'],
    'decentral-games': ['tournaments', 'reviews', 'system', 'roadmap'],
    'battle-for-giostone': ['tournaments', 'reviews', 'system', 'roadmap'],
    'time-breachers': ['tournaments', 'reviews', 'system', 'roadmap'],
    kittykart: ['tournaments', 'reviews', 'system', 'roadmap'],
    uldor: ['tournaments', 'reviews', 'system', 'roadmap'],
    pulsar: ['tournaments', 'reviews', 'system', 'roadmap'],
    'mighty-action-heroes': ['tournaments', 'reviews', 'roadmap'],
    'spider-tanks': ['tournaments', 'reviews', 'roadmap'],
    'axie-infinity-origins': ['tournaments', 'reviews', 'roadmap'],
    acryptia: ['tournaments', 'reviews', 'system', 'roadmap'],
    warsaken: ['tournaments', 'reviews', 'system', 'roadmap'],
    avalon: ['tournaments', 'reviews', 'system', 'roadmap'],
    crittercraft: ['tournaments', 'reviews', 'system', 'roadmap'],
    'dark-country': ['tournaments', 'reviews', 'system', 'roadmap'],
    'blockchain-brawlers': ['tournaments', 'reviews', 'system', 'roadmap'],
    'the-wake-ragnaroks-labyrinth': [
      'tournaments',
      'reviews',
      'system',
      'roadmap',
    ],
    cybertitans: ['tournaments', 'reviews', 'system', 'roadmap'],
    cyball: ['tournaments', 'reviews', 'system', 'roadmap'],
    magiccraft: ['tournaments', 'reviews', 'system', 'roadmap'],
    chesslers: ['tournaments', 'reviews', 'system', 'roadmap'],
    'citizen-conflict': ['tournaments', 'reviews', 'system', 'roadmap'],
    bushi: ['tournaments', 'reviews', 'system', 'roadmap'],
    mirandus: ['tournaments', 'reviews', 'system', 'roadmap'],
    superior: ['tournaments', 'reviews', 'system', 'roadmap'],
    'rune-seeker': ['tournaments', 'reviews', 'system', 'roadmap'],
    'champions-arena': ['tournaments', 'reviews', 'system', 'roadmap'],
    'eternal-paradox': ['tournaments', 'reviews', 'system', 'roadmap'],
    'katana-inu': ['tournaments', 'reviews', 'system', 'roadmap'],
    'last-expedition': ['tournaments', 'reviews', 'system', 'roadmap'],
    'mecha-world': ['tournaments', 'reviews', 'system', 'roadmap'],
    'rage-effect': ['tournaments', 'reviews', 'system', 'roadmap'],
    'starfall-arena': ['tournaments', 'reviews', 'system', 'roadmap'],
    'tollan-worlds': ['tournaments', 'reviews', 'system', 'roadmap'],
  };
  const games = await db
    .collection('Game')
    .where('title', '!=', 'BluMint')
    .get();
  const ids = games.docs.map((doc) => {
    return doc.get('id');
  });
  const paths = ids
    .filter((id) => {
      return id && TABS_MAP[`${id}`];
    })
    .map((id) => {
      return TABS_MAP[`${id}`].map((tab) => {
        return {
          params: { pid: id, tab },
        };
      });
    })
    .flat();
  // return { paths, fallback: 'blocking' };
  return { paths, fallback: false };
}

export { NestedGamePage as default };
